import React from "react";
import { TFunction } from "i18next";
import {
  FaClipboardList,
  FaDownload,
  FaMapMarkerAlt,
  FaPaperPlane,
  FaSync,
  FaTrashAlt,
  FaChevronDown,
  FaChevronUp,
  FaExclamationTriangle,
} from "react-icons/fa";
import Modal from "../../components/Modal";
import InlineRangePicker from "./InlineRangePicker";
import { formatDateLabel } from "./dateUtils";
import { UseOutingsReturn } from "./useOutings";

interface OutingActionsButtonProps {
  outings: UseOutingsReturn;
  classroomId: string;
  t: TFunction;
  canManage: boolean;
}

export const OutingActionsButton: React.FC<OutingActionsButtonProps> = ({ outings, classroomId, t, canManage }) => {
  const { outingMenuButtonRef, outingMenuRef } = outings;

  return (
    <div className="relative">
      <button
        ref={outingMenuButtonRef}
        type="button"
        onClick={() => outings.setShowOutingMenu((prev) => !prev)}
        className="lumen-glow-sheen inline-flex items-center gap-2 rounded-full border border-indigo-200/70 bg-indigo-50/80 px-4 py-2 text-xs font-semibold text-indigo-700 transition hover:text-indigo-900 dark:border-indigo-600/50 dark:bg-indigo-950/40 dark:text-indigo-100"
      >
        <FaMapMarkerAlt />
        <span>{t("classrooms.details.outing_menu_label", { defaultValue: "Outings and forms" })}</span>
      </button>
      {outings.showOutingMenu && (
        <div
          ref={outingMenuRef}
          className="absolute right-0 z-20 mt-2 w-64 rounded-2xl border border-indigo-100 bg-white/95 p-2 shadow-2xl backdrop-blur dark:border-indigo-700/60 dark:bg-gray-900/95"
        >
          <button
            type="button"
            onClick={() => {
              outings.setShowOutingMenu(false);
              outings.fetchOutingOverview();
              outings.setShowStatusPopover(false);
              outings.setTripActionFeedback(null);
              outings.setShowOutingOverviewModal(true);
            }}
            className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-gray-800 hover:bg-indigo-50 dark:text-gray-100 dark:hover:bg-gray-800/70"
          >
            <span className="text-indigo-600">
              <FaMapMarkerAlt />
            </span>
            {t("classrooms.details.outing_menu_overview", { defaultValue: "Outings and activities" })}
          </button>
          {canManage && (
            <button
              type="button"
              onClick={() => {
                outings.setOutingModalError(null);
                outings.setShowOutingModal(true);
                outings.setShowOutingMenu(false);
              }}
              className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-gray-800 hover:bg-emerald-50 dark:text-gray-100 dark:hover:bg-gray-800/70"
            >
            <span className="text-emerald-600">
              <FaClipboardList />
            </span>
              {t("classrooms.details.outing_menu_request", { defaultValue: "New outing" })}
          </button>
        )}
          <button
            type="button"
            onClick={() => {
              outings.setShowDownloadModal(true);
              outings.loadDownloadOptions(classroomId);
              outings.setShowOutingMenu(false);
            }}
            className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-gray-800 hover:bg-indigo-50 dark:text-gray-100 dark:hover:bg-gray-800/70"
          >
            <span className="text-indigo-600">
              <FaDownload />
            </span>
            {t("classrooms.details.outing_menu_history", { defaultValue: "Download forms" })}
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Column of a child in the outing overview. A completed form is not consent by itself:
 * the parent's answer (tripConsent) decides between "May join" and "May not join".
 */
export const consentColumn = (c: { status?: string | null; tripConsent?: boolean }): "yes" | "no" | "conflict" | "open" => {
  const s = (c.status || "").toUpperCase();
  if (s === "CONFLICT") return "conflict";
  if (s === "APPROVED") return "yes";
  if (s === "DENIED" || s === "REJECTED") return "no";
  if (s === "PENDING" || s === "PARTIALLY_COMPLETED") return "open";
  if (c.tripConsent === true) return "yes";
  if (c.tripConsent === false) return "no";
  return "open";
};

interface OutingOverviewCardProps {
  outings: UseOutingsReturn;
  t: TFunction;
  calendarItems?: ClassroomCalendarOverviewItem[];
  calendarLoading?: boolean;
  initialVisibleCount?: number;
}

export interface ClassroomCalendarOverviewItem {
  id: string;
  title: string;
  description?: string | null;
  eventType?: string | null;
  startDate?: string | number[] | null;
  endDate?: string | number[] | null;
  allDay?: boolean;
}

const formatCalendarDateLabel = (value: string | number[] | null | undefined) => {
  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day] = value;
    return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString("nl-NL", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }
  return formatDateLabel(value as string | undefined);
};

const toCalendarMillis = (value: string | number[] | null | undefined) => {
  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day, hour = 0, minute = 0, second = 0] = value;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    ).getTime();
  }
  if (!value) return Number.POSITIVE_INFINITY;
  const time = new Date(String(value)).getTime();
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
};

export const OutingOverviewCard: React.FC<OutingOverviewCardProps> = ({
  outings,
  t,
  calendarItems = [],
  calendarLoading = false,
  initialVisibleCount = 3,
}) => {
  const [showAllActivities, setShowAllActivities] = React.useState(false);
  const combinedItems = [
    ...calendarItems.map((item) => ({
      kind: "calendar" as const,
      key: `calendar-${item.id}`,
      startsAt: item.startDate,
      item,
    })),
    ...outings.sortedOutings.map((outing) => ({
      kind: "outing" as const,
      key: `outing-${outing.key}`,
      startsAt: outing.startsAt || outing.expiresAt,
      item: outing,
    })),
  ].sort((a, b) => toCalendarMillis(a.startsAt) - toCalendarMillis(b.startsAt));

  if (!combinedItems.length) {
    return (
      <p className="mt-2 rounded-xl border border-gray-200/70 bg-gray-50 px-3 py-2 text-sm text-gray-600 dark:border-gray-700/50 dark:bg-gray-800/50 dark:text-gray-200">
        {calendarLoading
          ? t("common.loading", { defaultValue: "Loading..." })
          : t("classroomDetails.agendaActivities.empty", {
              defaultValue: "No agenda items or outings with forms found.",
            })}
      </p>
    );
  }

  const visibleItems = showAllActivities ? combinedItems : combinedItems.slice(0, initialVisibleCount);

  return (
    <div className="mt-3 space-y-2">
      {visibleItems.map((entry) => {
        if (entry.kind === "calendar") {
          const item = entry.item;
          const dateParts = [formatCalendarDateLabel(item.startDate), formatCalendarDateLabel(item.endDate)].filter(Boolean);
          const range =
            dateParts.length === 0
              ? t("classrooms.details.trip_download_no_dates", { defaultValue: "No dates" })
              : dateParts.length === 1 || dateParts[0] === dateParts[1]
              ? dateParts[0]
              : dateParts.join(` ${t("classrooms.details.trip_download_range_sep", { defaultValue: "to" })} `);

          return (
            <div
              key={entry.key}
              className="rounded-2xl border border-sky-100/80 bg-sky-50/50 px-3 py-2.5 dark:border-sky-800/40 dark:bg-sky-950/20"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-sky-100 px-2.5 py-1 text-[11px] font-semibold text-sky-700 dark:bg-sky-900/50 dark:text-sky-100">
                  {t("classroomDetails.agendaActivities.badges.calendarItem", {
                    defaultValue: "Calendar item",
                  })}
                </span>
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {item.title || t("schoolCalendar.untitled", { defaultValue: "Untitled" })}
                </span>
                <span className="ml-auto text-xs text-gray-600 dark:text-gray-300">{range}</span>
              </div>
              {item.description && (
                <p className="mt-1 line-clamp-2 text-xs text-gray-600 dark:text-gray-300">{item.description}</p>
              )}
            </div>
          );
        }

        const outing = entry.item;
        return (
          <div
            key={entry.key}
            className="rounded-2xl border border-emerald-100/80 bg-emerald-50/50 px-3 py-2.5 dark:border-emerald-800/40 dark:bg-emerald-950/20"
          >
            <div className="flex flex-wrap items-center gap-2 md:flex-nowrap">
              <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-100">
                {t("classroomDetails.agendaActivities.badges.outingWithForms", {
                  defaultValue: "Outing with forms",
                })}
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {outing.name || t("classrooms.details.trip_download_unnamed", { defaultValue: "Untitled outing" })}
              </span>
              <span className="text-xs text-gray-600 dark:text-gray-300">
                {[formatDateLabel(outing.startsAt), formatDateLabel(outing.expiresAt)]
                  .filter(Boolean)
                  .join(` ${t("classrooms.details.trip_download_range_sep", { defaultValue: "to" })} `) ||
                  t("classrooms.details.trip_download_no_dates", { defaultValue: "No dates" })}
              </span>
              <div className="relative ml-auto">
                <button
                  type="button"
                  onClick={() => {
                    outings.setStatusPopoverOuting(outing);
                    outings.setShowStatusPopover((prev) => !prev);
                  }}
                  className="whitespace-nowrap rounded-full bg-white/80 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 shadow-sm transition hover:shadow-md dark:bg-emerald-900/40 dark:text-emerald-100"
                >
                  {t("classroomDetails.agendaActivities.badges.filled", {
                    defaultValue: "Filled: {{count}}/{{total}}",
                    count: outing.filled,
                    total: outing.total,
                  })}
                </button>
                {outings.showStatusPopover && outings.statusPopoverOuting?.key === outing.key && (
                  <div
                    ref={outings.statusPopoverRef}
                    className="absolute right-0 top-full z-20 mt-1 w-64 rounded-2xl border border-gray-200/80 bg-white p-2.5 shadow-xl ring-1 ring-black/5 dark:border-gray-700/60 dark:bg-gray-900"
                  >
                    <div className="mb-1.5">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {outings.statusPopoverOuting.name ||
                          t("classrooms.details.trip_download_unnamed", { defaultValue: "Untitled outing" })}
                      </p>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400">
                        {[
                          formatDateLabel(outings.statusPopoverOuting.startsAt),
                          formatDateLabel(outings.statusPopoverOuting.expiresAt),
                        ]
                          .filter(Boolean)
                          .join(` ${t("classrooms.details.trip_download_range_sep", { defaultValue: "to" })} `) ||
                          t("classrooms.details.trip_download_no_dates", { defaultValue: "No dates" })}
                      </p>
                    </div>
                    <div className="space-y-1 text-[13px] text-gray-800 dark:text-gray-200">
                      {outings.statusPopoverOuting.children?.length ? (
                        outings.statusPopoverOuting.children.map((c) => {
                          const statusKey = (c.status || "").toUpperCase();
                          const symbol =
                            statusKey === "CONFLICT"
                              ? "!"
                              : c.tripConsent === true
                              ? "✓"
                              : c.tripConsent === false
                              ? "✗"
                              : "?";
                          const color =
                            statusKey === "CONFLICT"
                              ? "text-purple-700"
                              : c.tripConsent === true
                              ? "text-emerald-700"
                              : c.tripConsent === false
                              ? "text-red-600"
                              : "text-gray-500";
                          const ring =
                            statusKey === "CONFLICT"
                              ? "border-purple-400 bg-purple-50"
                              : c.tripConsent === true
                              ? "border-emerald-400 bg-emerald-50"
                              : c.tripConsent === false
                              ? "border-red-400 bg-red-50"
                              : "border-gray-300 bg-gray-100";
                          return (
                            <div
                              key={`${outings.statusPopoverOuting?.key}-${c.childId || c.childName}`}
                              className="flex items-center justify-between rounded-lg px-2 py-1"
                            >
                              <span>{c.childName || t("common.unknown_child", { defaultValue: "Child" })}</span>
                              <span className={`flex h-7 w-7 items-center justify-center rounded-full border text-base font-semibold ${ring} ${color}`}>
                                {symbol}
                              </span>
                            </div>
                          );
                        })
                      ) : (
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {t("classrooms.details.outing_overview_empty_children", {
                            defaultValue: "No children for this outing yet",
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {combinedItems.length > initialVisibleCount && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-1 pt-1">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {t("classroomDetails.activities.visibleCount", {
              defaultValue: "Showing {{visible}} of {{total}}",
              visible: visibleItems.length,
              total: combinedItems.length,
            })}
          </span>
          <button
            type="button"
            onClick={() => setShowAllActivities((current) => !current)}
            className="lumen-glow-sheen rounded-full border border-indigo-200/70 bg-indigo-50/80 px-3 py-1 text-xs font-semibold text-indigo-700 transition hover:text-indigo-900 dark:border-indigo-700/50 dark:bg-indigo-950/40 dark:text-indigo-100"
          >
            {showAllActivities
              ? t("classroomDetails.activities.showLess", { defaultValue: "Show less" })
              : t("classroomDetails.activities.showMore", { defaultValue: "Show more" })}
          </button>
        </div>
      )}
    </div>
  );
};

interface OutingModalsProps {
  outings: UseOutingsReturn;
  t: TFunction;
  canManage: boolean;
}

export const OutingModals: React.FC<OutingModalsProps> = ({ outings, t, canManage }) => {
  const { sortedOutings } = outings;

  return (
    <>
      {canManage && (
        <Modal
          show={outings.showOutingModal}
          title={t("classrooms.details.request_trip_forms", { defaultValue: "Vraag toestemmingsformulieren op" })}
          message={null}
          icon={
            <span className="text-indigo-500 text-5xl">
              <FaPaperPlane />
            </span>
          }
          hideDefaultActions
          dialogClassName="!w-[min(92vw,34rem)] !max-w-none"
          onClose={() => outings.setShowOutingModal(false)}
          footer={
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
              <button
                onClick={() => outings.setShowOutingModal(false)}
                className="rounded-full border border-gray-200/70 bg-white/80 px-4 py-2 text-sm font-semibold text-gray-700 hover:text-gray-900 dark:border-gray-700/60 dark:bg-gray-800/70 dark:text-gray-200"
              >
                {t("common.actions.cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                onClick={outings.submitOutingRequest}
                disabled={outings.tripRequestLoading}
                className="lumen-glow-sheen inline-flex items-center justify-center rounded-full border border-indigo-300/80 bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-500 disabled:cursor-wait disabled:opacity-80 dark:border-indigo-800/60 dark:bg-indigo-700 dark:hover:bg-indigo-600"
              >
                {outings.tripRequestLoading
                  ? t("classrooms.details.trip_request_loading", { defaultValue: "Working..." })
                  : t("classrooms.details.trip_request_send", { defaultValue: "Send request" })}
              </button>
            </div>
          }
        >
          <div className="space-y-4 mb-3">
            <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-200">
              {t("classrooms.details.trip_request_modal_intro", {
                defaultValue:
                  "Give this outing an optional name and set a period. Leave empty if you do not want to use a start or end date.",
              })}
            </p>
            <div className="rounded-2xl border border-gray-200/70 bg-white/80 p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900/50">
              <label className="block text-sm font-semibold text-gray-800 dark:text-gray-100">
                {t("classrooms.details.trip_request_name", { defaultValue: "Outing name (optional)" })}
                <input
                  type="text"
                  value={outings.outingName}
                  onChange={(e) => outings.setOutingName(e.target.value)}
                  className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
                />
              </label>
              <div className="mt-3">
                <InlineRangePicker
                  valueStart={outings.outingStartsAt}
                  valueEnd={outings.outingExpiresAt}
                  onChange={(start, end) => {
                    outings.setOutingStartsAt(start);
                    outings.setOutingExpiresAt(end);
                  }}
                  t={t}
                />
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {t("classrooms.details.trip_request_deadline_hint", {
                  defaultValue: "After the end date, the request and completed data are hidden automatically.",
                })}
                <label className="mt-3 flex items-start gap-3 rounded-2xl border border-indigo-100 bg-indigo-50/60 p-3 text-sm text-gray-700 dark:border-indigo-800/50 dark:bg-indigo-950/30 dark:text-gray-200">
                  <input
                    type="checkbox"
                    checked={outings.outingAddToClassroomCalendar}
                    onChange={(event) =>
                      outings.setOutingAddToClassroomCalendar(event.target.checked)
                    }
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />

                  <span>
                    <span className="block font-semibold">
                      {t("classrooms.details.trip_add_to_classroom_calendar", {
                        defaultValue: "Also add to the classroom calendar",
                      })}
                    </span>
                    <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                      {t("classrooms.details.trip_add_to_classroom_calendar_hint", {
                        defaultValue:
                          "A calendar item will be created automatically for this classroom.",
                      })}
                    </span>
                  </span>
                </label>
              </p>
            </div>
            {outings.outingModalError && (
              <p className="rounded-lg border border-amber-200/70 bg-amber-50/80 px-3 py-2 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-900/30 dark:text-amber-100">
                {outings.outingModalError}
              </p>
            )}
          </div>
        </Modal>
      )}

      <Modal
        show={outings.showOutingOverviewModal}
        title={t("classrooms.details.outing_menu_overview", { defaultValue: "Outings and activities" })}
        message={null}
        icon={
          <span className="text-indigo-500 text-5xl">
            <FaMapMarkerAlt />
          </span>
        }
        hideDefaultActions
        dialogClassName="!w-[min(95vw,70rem)] !max-w-none max-h-[85vh] overflow-y-auto text-left"
        onClose={() => outings.setShowOutingOverviewModal(false)}
        footer={
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => outings.setShowOutingOverviewModal(false)}
              className="rounded-full border border-gray-200/70 bg-white/80 px-4 py-2 text-sm font-semibold text-gray-700 hover:text-gray-900 dark:border-gray-700/60 dark:bg-gray-800/70 dark:text-gray-200"
            >
              {t("common.actions.close", { defaultValue: "Close" })}
            </button>
          </div>
        }
      >
        {outings.tripActionFeedback && (
          <p
            role="status"
            className={`mb-3 rounded-xl px-3 py-2 text-sm ${
              outings.tripActionFeedback.type === "success"
                ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200"
                : "bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
            }`}
          >
            {outings.tripActionFeedback.message}
          </p>
        )}
        {outings.outingOverviewLoading ? (
          <p className="text-sm text-gray-700 dark:text-gray-200">
            {t("common.loading", { defaultValue: "Loading..." })}
          </p>
        ) : outings.outingOverview.length === 0 ? (
          <p className="text-sm text-gray-700 dark:text-gray-200">
            {t("classrooms.details.outing_overview_empty", {
              defaultValue: "No outings with forms found yet.",
            })}
          </p>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-end gap-2">
              {canManage && (
                <button
                  type="button"
                  onClick={() => outings.setShowOutingModal(true)}
                  className="inline-flex items-center gap-2 rounded-full border border-indigo-200/70 bg-indigo-50/80 px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm hover:text-indigo-900 dark:border-indigo-700/60 dark:bg-indigo-950/40 dark:text-indigo-100"
                >
                  {t("classrooms.details.outing_menu_request", { defaultValue: "New outing" })}
                </button>
              )}
            </div>
            {(sortedOutings.length ? sortedOutings : outings.outingOverview).map((outingItem) => {
              const dateParts = [formatDateLabel(outingItem.startsAt), formatDateLabel(outingItem.expiresAt)].filter(Boolean);
              const range =
                dateParts.length === 0
                  ? t("classrooms.details.trip_download_no_dates", { defaultValue: "No dates" })
                  : dateParts.length === 1
                  ? dateParts[0]
                  : dateParts.join(` ${t("classrooms.details.trip_download_range_sep", { defaultValue: "t/m" })} `);
              const expanded = outings.outingStatusExpanded[outingItem.key];
              const childrenYes = outingItem.children.filter((c) => consentColumn(c) === "yes");
              const childrenNo = outingItem.children.filter((c) => consentColumn(c) === "no");
              const childrenConflict = outingItem.children.filter((c) => consentColumn(c) === "conflict");
              const childrenOpen = outingItem.children.filter((c) => consentColumn(c) === "open");
              const gridCols = childrenConflict.length
                ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-4"
                : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3";
              const toggleExpanded = () =>
                outings.setOutingStatusExpanded((prev) => {
                  const nextExpanded = !prev[outingItem.key];
                  if (!nextExpanded) return {};
                  return { [outingItem.key]: true };
                });
              const openDownloadForOuting = () => {
                const opt = {
                  key: outingItem.key,
                  outingId: outingItem.outingId,
                  name: outingItem.name,
                  startsAt: outingItem.startsAt,
                  expiresAt: outingItem.expiresAt,
                };
                outings.setSelectedDownloadOption(opt);
                outings.setShowDownloadModal(true);
                outings.loadDownloadOptions(undefined, opt);
              };
              const needsSecondConsent =
                outingItem.requiresDualConsent &&
                (outingItem.consentsGiven ?? 0) < (outingItem.consentsRequired ?? 2);
              const hasDenial = outingItem.children?.some((c) => c.tripConsent === false);
              return (
                <div
                  key={`card-${outingItem.key}`}
                  className="rounded-3xl border border-indigo-100/70 bg-white/95 p-4 shadow-[0_20px_50px_rgba(15,23,42,0.08)] dark:border-indigo-800/40 dark:bg-gray-900/70"
                >
                  <div
                    className="flex cursor-pointer flex-wrap items-center gap-3"
                    role="button"
                    tabIndex={0}
                    onClick={toggleExpanded}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleExpanded();
                      }
                    }}
                  >
                    <div className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/50">
                        🚌
                      </span>
                      <span>{outingItem.name || t("classrooms.details.trip_download_unnamed", { defaultValue: "Unnamed outing" })}</span>
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-300">{range}</span>
                    <div className="ml-auto flex flex-wrap items-center gap-2">
                      {outingItem.requiresDualConsent && (
                        <span
                          className={`rounded-full px-2.5 py-1 text-[12px] font-semibold ${
                            hasDenial
                              ? "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-100"
                              : needsSecondConsent
                              ? "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-100"
                              : "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100"
                          }`}
                        >
                          {hasDenial ? (
                            <span className="inline-flex items-center gap-1">
                              <span className="text-rose-600">
                                <FaExclamationTriangle />
                              </span>
                              {t("classrooms.details.outing_dual_denied", { defaultValue: "Denied" })}
                            </span>
                          ) : needsSecondConsent ? (
                            t("classrooms.details.outing_dual_partial", { defaultValue: "Waiting for parent 2" })
                          ) : (
                            t("classrooms.details.outing_dual_complete", { defaultValue: "Both parents approved" })
                          )}
                        </span>
                      )}
                      <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[12px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-100">
                        {t("classrooms.details.outing_overview_consent_yes", {
                          defaultValue: "May join: {{count}}",
                          count: childrenYes.length,
                        })}
                      </span>
                      <span className="rounded-full bg-rose-50 px-2.5 py-1 text-[12px] font-semibold text-rose-700 dark:bg-rose-900/40 dark:text-rose-100">
                        {t("classrooms.details.outing_overview_consent_no", {
                          defaultValue: "May not join: {{count}}",
                          count: childrenNo.length,
                        })}
                      </span>
                      <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[12px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-100">
                        {t("classrooms.details.trip_download_status_missing", { defaultValue: "Open: {{count}}" , count: childrenOpen.length })}
                      </span>
                      {childrenConflict.length > 0 && (
                        <span className="rounded-full bg-purple-50 px-2.5 py-1 text-[12px] font-semibold text-purple-700 dark:bg-purple-900/40 dark:text-purple-100">
                          {t("classrooms.details.outing_overview_consent_conflict", {
                            defaultValue: "Conflict: {{count}}",
                            count: childrenConflict.length,
                          })}
                        </span>
                      )}
                      <span className="inline-flex items-center rounded-full border border-indigo-200/80 bg-indigo-50/80 px-2.5 py-1 text-xs font-semibold text-indigo-700 shadow-sm dark:border-indigo-700/60 dark:bg-indigo-950/40 dark:text-indigo-100">
                        {expanded ? <FaChevronUp /> : <FaChevronDown />}
                        <span className="sr-only">
                          {expanded
                            ? t("classrooms.details.hide_status", { defaultValue: "Hide status" })
                            : t("classrooms.details.show_status", { defaultValue: "Show status" })}
                        </span>
                      </span>
                    </div>
                  </div>

                  {expanded && (
                    <div className="mt-4 space-y-3">
                      <div className={`grid gap-2 ${gridCols}`}>
                        <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-3 dark:border-emerald-800/40 dark:bg-emerald-900/30">
                          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-100">
                            {t("classrooms.details.trip_download_consent_yes", { defaultValue: "May join" })} ({childrenYes.length})
                          </p>
                          {childrenYes.length ? (
                            <div className="mt-2 space-y-1">
                              {childrenYes.map((c) => (
                                <div
                                  key={`${outingItem.key}-yes-${c.childId || c.childName}`}
                                  className="flex items-center justify-between gap-2 rounded-xl bg-white/70 px-3 py-2 text-sm text-gray-900 shadow-sm dark:bg-emerald-950/40 dark:text-emerald-50"
                                >
                                  <span className="min-w-0 truncate">{c.childName || t("common.unknown_child", { defaultValue: "Child" })}</span>
                                  {canManage && (
                                    <div className="flex shrink-0 items-center gap-1">
                                      <button
                                        type="button"
                                        onClick={() => outings.reRequestOutingForChild(c.childId as string, outingItem, c.requestId)}
                                        disabled={outings.outingActionLoading}
                                        className="rounded-full bg-white/70 px-1 text-[11px] text-indigo-700 hover:text-indigo-900 dark:bg-emerald-900/40 dark:text-emerald-100"
                                        title={t("classrooms.details.outing_child_rerequest", { defaultValue: "Remind" })}
                                      >
                                        <FaSync />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => outings.deleteOutingForChild(c.childId, outingItem)}
                                        disabled={outings.outingActionLoading}
                                        className="rounded-full bg-white/70 px-1 text-[11px] text-red-600 hover:text-red-800 dark:bg-emerald-900/40 dark:text-red-100"
                                        title={t("classrooms.details.outing_child_delete", { defaultValue: "Delete" })}
                                      >
                                        <FaTrashAlt />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="mt-2 text-sm text-emerald-800/80 dark:text-emerald-100">
                              {t("classrooms.details.outing_overview_empty_children", { defaultValue: "No children" })}
                            </p>
                          )}
                        </div>
                        {childrenConflict.length > 0 && (
                          <div className="rounded-2xl border border-purple-100 bg-purple-50/70 p-3 dark:border-purple-800/40 dark:bg-purple-900/30">
                            <p className="text-xs font-semibold uppercase tracking-wide text-purple-700 dark:text-purple-100">
                              {t("classrooms.details.trip_download_consent_conflict", { defaultValue: "Conflict" })} ({childrenConflict.length})
                            </p>
                            <div className="mt-2 space-y-1">
                              {childrenConflict.map((c) => (
                                <div
                                  key={`${outingItem.key}-conflict-${c.childId || c.childName}`}
                                  className="flex items-center justify-between gap-2 rounded-xl bg-white/70 px-3 py-2 text-sm text-gray-900 shadow-sm dark:bg-purple-950/40 dark:text-purple-50"
                                >
                                  <span className="min-w-0 truncate">
                                    {c.childName || t("common.unknown_child", { defaultValue: "Child" })}
                                  </span>
                                  {canManage && (
                                    <div className="flex shrink-0 items-center gap-1">
                                      <button
                                        type="button"
                                        onClick={() => outings.reRequestOutingForChild(c.childId as string, outingItem, c.requestId)}
                                        disabled={outings.outingActionLoading}
                                        className="rounded-full bg-white/70 px-1 text-[11px] text-indigo-700 hover:text-indigo-900 dark:bg-purple-900/40 dark:text-indigo-100"
                                        title={t("classrooms.details.outing_child_rerequest", { defaultValue: "Remind" })}
                                      >
                                        <FaSync />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => outings.deleteOutingForChild(c.childId, outingItem)}
                                        disabled={outings.outingActionLoading}
                                        className="rounded-full bg-white/70 px-1 text-[11px] text-red-600 hover:text-red-800 dark:bg-purple-900/40 dark:text-red-100"
                                        title={t("classrooms.details.outing_child_delete", { defaultValue: "Delete" })}
                                      >
                                        <FaTrashAlt />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="rounded-2xl border border-rose-100 bg-rose-50/70 p-3 dark:border-rose-800/40 dark:bg-rose-900/30">
                          <p className="text-xs font-semibold uppercase tracking-wide text-rose-700 dark:text-rose-100">
                            {t("classrooms.details.trip_download_consent_no", { defaultValue: "May not join" })} ({childrenNo.length})
                          </p>
                          {childrenNo.length ? (
                            <div className="mt-2 space-y-1">
                              {childrenNo.map((c) => (
                                <div
                                  key={`${outingItem.key}-no-${c.childId || c.childName}`}
                                  className="flex items-center justify-between gap-2 rounded-xl bg-white/70 px-3 py-2 text-sm text-gray-900 shadow-sm dark:bg-rose-950/40 dark:text-rose-50"
                                >
                                  <span className="min-w-0 truncate">{c.childName || t("common.unknown_child", { defaultValue: "Child" })}</span>
                                  {canManage && (
                                    <div className="flex shrink-0 items-center gap-1">
                                      <button
                                        type="button"
                                        onClick={() => outings.reRequestOutingForChild(c.childId as string, outingItem, c.requestId)}
                                        disabled={outings.outingActionLoading}
                                        className="rounded-full bg-white/70 px-1 text-[11px] text-indigo-700 hover:text-indigo-900 dark:bg-rose-900/40 dark:text-rose-100"
                                        title={t("classrooms.details.outing_child_rerequest", { defaultValue: "Remind" })}
                                      >
                                        <FaSync />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => outings.deleteOutingForChild(c.childId, outingItem)}
                                        disabled={outings.outingActionLoading}
                                        className="rounded-full bg-white/70 px-1 text-[11px] text-red-600 hover:text-red-800 dark:bg-rose-900/40 dark:text-red-100"
                                        title={t("classrooms.details.outing_child_delete", { defaultValue: "Delete" })}
                                      >
                                        <FaTrashAlt />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="mt-2 text-sm text-rose-800/80 dark:text-rose-100">
                              {t("classrooms.details.outing_overview_empty_children", { defaultValue: "No children" })}
                            </p>
                          )}
                        </div>

                        <div className="rounded-2xl border border-amber-100 bg-amber-50/70 p-3 dark:border-amber-800/40 dark:bg-amber-900/30">
                          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-100">
                            {t("classrooms.details.trip_download_consent_unknown", { defaultValue: "Open" })} ({childrenOpen.length})
                          </p>
                          {childrenOpen.length ? (
                            <div className="mt-2 space-y-1">
                              {childrenOpen.map((c) => (
                                <div
                                  key={`${outingItem.key}-open-${c.childId || c.childName}`}
                                  className="flex items-center justify-between gap-2 rounded-xl bg-white/70 px-3 py-2 text-sm text-gray-900 shadow-sm dark:bg-amber-950/40 dark:text-amber-50"
                                >
                                  <span className="min-w-0 truncate">{c.childName || t("common.unknown_child", { defaultValue: "Child" })}</span>
                                  {canManage && (
                                    <div className="flex shrink-0 items-center gap-1">
                                      <button
                                        type="button"
                                        onClick={() => outings.reRequestOutingForChild(c.childId as string, outingItem, c.requestId)}
                                        disabled={outings.outingActionLoading}
                                        className="rounded-full bg-white/70 px-2 py-1 text-[11px] text-amber-700 hover:text-amber-900 dark:bg-amber-900/40 dark:text-amber-100"
                                        title={t("classrooms.details.outing_child_rerequest", { defaultValue: "Remind" })}
                                      >
                                        {t("classrooms.details.outing_child_rerequest", { defaultValue: "Remind" })}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => outings.deleteOutingForChild(c.childId, outingItem)}
                                        disabled={outings.outingActionLoading}
                                        className="rounded-full bg-white/70 px-1 text-[11px] text-red-600 hover:text-red-800 dark:bg-amber-900/40 dark:text-red-100"
                                        title={t("classrooms.details.outing_child_delete", { defaultValue: "Delete" })}
                                      >
                                        <FaTrashAlt />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="mt-2 text-sm text-amber-800/80 dark:text-amber-100">
                              {t("classrooms.details.outing_overview_empty_children", { defaultValue: "No children" })}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        {canManage && outingItem.outingId && (
                          <button
                            type="button"
                            onClick={() => outings.deleteOutingById(outingItem.outingId)}
                            disabled={outings.outingActionLoading}
                            className="inline-flex items-center gap-2 rounded-full border border-rose-200/70 bg-rose-50/80 px-4 py-2 text-sm font-semibold text-rose-700 shadow-sm transition hover:border-rose-300 hover:bg-rose-100 hover:text-rose-900 disabled:opacity-70 dark:border-rose-800/60 dark:bg-rose-900/30 dark:text-rose-100 dark:hover:border-rose-700 dark:hover:bg-rose-900/50"
                          >
                            {t("classrooms.details.outing_delete", { defaultValue: "Delete outing" })}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={openDownloadForOuting}
                          className="inline-flex items-center gap-2 rounded-full border border-indigo-200/70 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-50 hover:text-indigo-900 dark:border-indigo-700/60 dark:bg-indigo-950/40 dark:text-indigo-100 dark:hover:bg-indigo-900/60"
                        >
                          {t("classrooms.details.download_trip_forms", { defaultValue: "Download forms" })}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Modal>

      <Modal
        show={outings.showDownloadModal}
        title={t("classrooms.details.download_trip_forms", { defaultValue: "Download forms" })}
        message={null}
        icon={
          <span className="text-indigo-500 text-5xl">
            <FaDownload />
          </span>
        }
        hideDefaultActions
        dialogClassName="!w-[min(92vw,36rem)] !max-w-none"
        onClose={() => outings.setShowDownloadModal(false)}
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
            <button
              onClick={() => outings.setShowDownloadModal(false)}
              className="rounded-full border border-gray-200/70 bg-white/80 px-4 py-2 text-sm font-semibold text-gray-700 hover:text-gray-900 dark:border-gray-700/60 dark:bg-gray-800/70 dark:text-gray-200"
            >
                {t("common.actions.cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              onClick={outings.handleDownloadTripForms}
              disabled={outings.tripExportLoading}
              className="lumen-glow-sheen inline-flex items-center justify-center rounded-full border border-indigo-300/80 bg-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-500 disabled:cursor-wait disabled:opacity-80 dark:border-indigo-800/60 dark:bg-indigo-700 dark:hover:bg-indigo-600"
            >
              {outings.tripExportLoading
                ? t("classrooms.details.trip_export_loading", { defaultValue: "Downloading..." })
                : t("classrooms.details.trip_export_start", { defaultValue: "Start download" })}
            </button>
          </div>
        }
      >
        <div className="space-y-4 mb-3">
          <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-200">
            {t("classrooms.details.trip_download_intro", {
              defaultValue: "Choose which outing you want to download.",
            })}
          </p>
          <div className="rounded-2xl border border-gray-200/70 bg-white/80 p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900/50">
            {outings.downloadOptionsLoading && (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t("classrooms.details.trip_download_loading", { defaultValue: "Loading..." })}
              </p>
            )}
            {!outings.downloadOptionsLoading && outings.downloadOptions.length === 0 && (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t("classrooms.details.trip_download_none", {
                  defaultValue: "No outings available to download.",
                })}
              </p>
            )}
            {!outings.downloadOptionsLoading && outings.downloadOptions.length > 0 && (
              <div className="space-y-2">
                {outings.downloadOptions.map((opt) => {
                  const displayName =
                    opt.name || t("classrooms.details.trip_download_unnamed", { defaultValue: "Unnamed outing" });
                  const dateParts = [formatDateLabel(opt.startsAt), formatDateLabel(opt.expiresAt)].filter(Boolean);
                  const range =
                    dateParts.length === 0
                      ? t("classrooms.details.trip_download_no_dates", { defaultValue: "No dates" })
                      : dateParts.length === 1
                      ? dateParts[0]
                      : dateParts.join(` ${t("classrooms.details.trip_download_range_sep", { defaultValue: "t/m" })} `);
                  const labelParts = [displayName, range].filter(Boolean);
                  return (
                    <label
                      key={opt.key}
                      className="flex items-start gap-2 rounded-lg border border-transparent px-2 py-2 hover:border-indigo-200 dark:hover:border-indigo-700"
                    >
                      <input
                        type="radio"
                        name="trip-download"
                        checked={outings.selectedDownloadOption?.key === opt.key}
                        onChange={() => {
                          outings.setSelectedDownloadOption(opt);
                          outings.fetchOutingStatus(opt);
                        }}
                        className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-800 dark:text-gray-100">{labelParts.join(" • ")}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};
